export interface LandingHelpGuideTip {
    category: string;
    icon: string;
    title: string;
    description: string;
    linkText: string;
    linkUrl: string;
    weight?: number;
    featuredOnLanding?: boolean;
}

export interface HelpGuide {
    id: string;
    title: string;
    href: string;
    linkSuffix: string;
    description: string;
    strongIntro?: string;
    emphasis?: string;
    descriptionLink?: {
        href: string;
        text: string;
    };
    descriptionAfterLink?: string;
    landingTip?: Omit<LandingHelpGuideTip, 'linkUrl'>;
    /** Defaults to true. Set to false for guides intended only for the home screen. */
    showOnHelp?: boolean;
    /** Defaults to true when a landingTip is supplied. */
    showOnLanding?: boolean;
    /** Renders above the landing-page guide carousel instead of inside it. */
    featuredOnLanding?: boolean;
    /** ISO date (YYYY-MM-DD). The guide is hidden on and after this date. */
    expiresOn?: string;
}

export interface HelpGuideGroup {
    title?: string;
    guides: HelpGuide[];
}

export interface HelpGuideSection {
    title: string;
    description?: string;
    groups: HelpGuideGroup[];
}

export const isGuideActive = (guide: Pick<HelpGuide, 'expiresOn'>, now = new Date()) => {
    if (!guide.expiresOn) {
        return true;
    }

    const localDate = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0')
    ].join('-');

    return localDate < guide.expiresOn;
};

export const helpGuideSections: HelpGuideSection[] = [
    {
        title: 'Getting Started - New Organizations and Admins',
        groups: [
            {
                guides: [
                    {
                        id: 'countbubble-updates-october-2026',
                        title: 'COUNTBUBBLE UPDATES: OCTOBER 2026',
                        href: 'https://files.countbubble.com/guides/Countbubble+Updates+October+2026.pdf',
                        linkSuffix: '(pdf)',
                        description: 'See the latest countbubble improvements and updates.',
                        showOnHelp: false,
                        showOnLanding: true,
                        featuredOnLanding: true,
                        expiresOn: '2026-11-16',
                        landingTip: {
                            category: 'What\'s New',
                            icon: 'ri-megaphone-line',
                            title: 'COUNTBUBBLE UPDATES: OCTOBER 2026',
                            description: 'See the latest countbubble improvements and updates.',
                            linkText: 'Read the Updates'
                        }
                    },
                    {
                        id: 'quick-site-tour',
                        title: 'Quick Site Tour',
                        href: 'https://youtu.be/IXUN9ky48kE',
                        linkSuffix: '(Youtube video only)',
                        strongIntro: 'Start here if this is your first time using countbubble!',
                        description: 'Learn about key features and concepts in 4 minutes.',
                        landingTip: {
                            category: 'Getting Started',
                            icon: 'ri-rocket-line',
                            title: 'Quick Site Tour',
                            description: 'New to countbubble? Learn about key features and concepts in just 4 minutes.',
                            linkText: 'Watch Video'
                        }
                    },
                    {
                        id: 'new-customer-checklist',
                        title: 'New Customer Getting Started Checklist',
                        href: 'https://files.countbubble.com/guides/New+Customer+Getting+Started+Checklist.pdf',
                        linkSuffix: '(pdf)',
                        description: "It's hard to know how to start. This checklist will help new organizations get going with confidence."
                    },
                    {
                        id: 'getting-started-30-minutes',
                        title: 'Getting Started with countbubble in About 30 Minutes',
                        href: 'https://files.countbubble.com/guides/Getting+Started+with+countbubble+in+About+30+Min.pdf',
                        linkSuffix: '(pdf)',
                        description: 'In a hurry to get started? Not a "checklist person"? This How To guide will teach you to build a form, create a program, add a participant, and submit data. This is a great starting place for your free trial.'
                    }
                ]
            }
        ]
    },
    {
        title: 'Customize Your Site',
        groups: [
            {
                guides: [
                    {
                        id: 'manage-standard-demographics',
                        title: 'Manage Standard Demographics',
                        href: 'https://files.countbubble.com/guides/Manage+Standard+Demographics.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn how to choose the Gender, Race, and Ethnicity options that best fit your needs.',
                        emphasis: 'Includes link to a 6 minute video.',
                        landingTip: {
                            category: 'Site Setup',
                            icon: 'ri-settings-3-line',
                            title: 'Manage Standard Demographics',
                            description: 'Choose the gender, race, and ethnicity options that best fit your organization.',
                            linkText: 'View Guide',
                            weight: 2
                        }
                    },
                    {   id: 'manage-date-of-birth',
                        title: 'Manage Date of Birth Demographics',
                        href: 'https://files.countbubble.com/guides/Manage+Date+of+Birth.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn to make Date of Birth optional or required for participants.',
                        emphasis: 'Includes link to a 3.5 minute video.',
                        landingTip: {
                            category: 'Site Setup',
                            icon: 'ri-settings-3-line',
                            title: 'Manage Date of Birth Demographics',
                            description: 'Make Date of Birth optional or required for participants.',
                            linkText: 'View Guide',
                            weight: 2
                        }
                    }
                ]
            }
        ]
    },
    {
        title: 'Foundational Concepts',
        description: 'These are ideal for admin users and anyone who wants a deep understanding of countbubble concepts',
        groups: [
            {
                guides: [
                    {
                        id: 'key-countbubble-terms',
                        title: 'Key countbubble Terms',
                        href: 'https://files.countbubble.com/guides/Key+countbubble+Terms.pdf',
                        linkSuffix: '(pdf)',
                        description: "What's a program? What are user roles? What types of forms can I build in countbubble?"
                    },
                    {
                        id: 'site-organization',
                        title: 'countbubble Site Organization',
                        href: 'https://files.countbubble.com/guides/countbubble+Site+Organization.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn the basic elements of a countbubble site to understand how it can be used.'
                    },
                    {
                        id: 'form-field-types',
                        title: 'Quick Guide to Form Field Types',
                        href: 'https://files.countbubble.com/guides/Quick+Guide+to+Form+Field+Types.pdf',
                        linkSuffix: '(pdf)',
                        description: 'This guide describes the form field types available in the Form Builder & explains when/how each type is best used.'
                    }
                ]
            }
        ]
    },
    {
        title: 'How To Guides for Everyone',
        groups: [
            {
                guides: [
                    {
                        id: 'add-participant',
                        title: 'Add a Participant',
                        href: 'https://files.countbubble.com/guides/Add+a+Participant.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Add a new participant to your countbubble site.',
                        emphasis: 'Includes link to a 3 minute video.'
                    },
                    {
                        id: 'edit-participant-profile',
                        title: "Edit a Participant's Profile Information",
                        href: 'https://files.countbubble.com/guides/Edit+a+participant%E2%80%99s+profile+information.pdf',
                        linkSuffix: '(pdf)',
                        description: "Learn how to change a participant's phone number, address, family members, or something else on their profile.",
                        emphasis: 'Includes link to a 3 minute video.'
                    },
                    {
                        id: 'manage-duplicate-participants',
                        title: 'Managing Duplicated Participants',
                        href: 'https://files.countbubble.com/guides/Managing+Duplicated+Participants+in+countbubble.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn how to review and merge duplicate participants.',
                        emphasis: 'Includes link to a 6 minute video.'
                    },
                    {
                        id: 'search-participants',
                        title: 'Search for Participants',
                        href: 'https://files.countbubble.com/guides/Search+for+Participants.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn how to find participants using the participant search bar.',
                        emphasis: 'Includes link to a 3 minute video.',
                        landingTip: {
                            category: 'Search Tips',
                            icon: 'ri-search-line',
                            title: 'Search for Participants',
                            description: 'Learn how to quickly find participants using the search bar and filters.',
                            linkText: 'View Guide'
                        }
                    },
                    {              
                        id: 'manage-inactive-participants',
                        title: 'Manage Inactive Participants',
                        href: 'https://files.countbubble.com/guides/Manage+Inactive+Participants.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn to quickly view and manage inactive participants.',
                        emphasis: 'Includes link to a 6 minute video.',
                        landingTip: {
                            category: 'Organization',
                            icon: 'ri-sticky-note-line',
                            title: 'Manage Inactive Participants',
                            description: 'Learn to quickly view and manage inactive participants.',
                            linkText: 'View Guide'
                        }
                    },
                    {
                        id: 'enroll-participant-program',
                        title: 'Enroll a Participant in a Program',
                        href: 'https://files.countbubble.com/guides/Enroll+a+Participant+in+a+Program.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn how and why to enroll a participant in a program.',
                        emphasis: 'Includes link to a 2 minute video.',
                        landingTip: {
                            category: 'Programs',
                            icon: 'ri-user-add-line',
                            title: 'Enroll Participants',
                            description: 'Learn how and why to enroll participants in programs for better tracking.',
                            linkText: 'View Guide'
                        }
                    },
                    {
                        id: 'track-outputs-outcomes-participant',
                        title: 'Track Outputs and Outcomes with a Participant',
                        href: 'https://files.countbubble.com/guides/Track+Outputs+or+Outcomes+with+a+Participant.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn how to quickly track data using Outputs and Outcomes forms.',
                        emphasis: 'Includes link to a 3 minute video.'
                    },
                    {
                        id: 'track-data-search-page',
                        title: 'Track Data from Participant Search Page',
                        href: 'https://files.countbubble.com/guides/Track+Data+from+Participant+Search+Page.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Save time and clicks! Learn to track data from the Participant Search page.',
                        emphasis: 'Includes link to a 2 minute video.',
                        landingTip: {
                            category: 'Data Entry',
                            icon: 'ri-file-list-3-line',
                            title: 'Track Data Faster',
                            description: 'Save time! Learn to track outputs and outcomes directly from the search page.',
                            linkText: 'View Guide'
                        }
                    },
                    {
                        id: 'track-household-members',
                        title: 'Track Data for Household Members',
                        href: 'https://files.countbubble.com/guides/Track+Data+for+Household+Members.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn to use Tracking Forms with multiple household members simultaneously.',
                        emphasis: 'Includes link to a 3 minute video.',
                        landingTip: {
                            category: 'Data Entry',
                            icon: 'ri-group-line',
                            title: 'Track Household Data',
                            description: 'Use tracking forms with multiple household members at the same time.',
                            linkText: 'View Guide',
                            weight: 4
                        }
                    },
                    {
                        id: 'basic-reports-exports',
                        title: 'Create Basic Reports and Exports',
                        href: 'https://files.countbubble.com/guides/Reports+and+Exports+in+countbubble.pdf',
                        linkSuffix: '(pdf)',
                        description: "Learn how to generate reports and data exports from the data you've stored.",
                        emphasis: 'Includes links to 6 videos.',
                        landingTip: {
                            category: 'Reporting',
                            icon: 'ri-bar-chart-box-line',
                            title: 'Create Reports & Exports',
                            description: "Generate reports and data exports from the data you've stored in countbubble.",
                            linkText: 'View Guide'
                        }
                    },
                    {
                        id: 'advanced-reports',
                        title: 'Guide to Advanced Reports',
                        href: 'https://files.countbubble.com/guides/Guide+to+Advanced+Reports.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn how to generate advanced reports using filters to target specific participants or services.',
                        emphasis: 'Includes links to 7 videos.'
                    },
                                        {
                        id: 'build-a-dashboard',
                        title: 'Build a Dashboard',
                        href: 'https://files.countbubble.com/guides/Build+a+Dashboard.pdf',
                        linkSuffix: '(pdf)',
                        description: "Learn to build simple, powerful Dashboards from your Reports.",
                        emphasis: 'Includes links to 5 videos.',
                        landingTip: {
                            category: 'Reporting',
                            icon: 'ri-bar-chart-box-line',
                            title: 'Build a Dashboard',
                            description: "Learn to create a Dashboard to see your most important data quickly.",
                            linkText: 'View Guide',
                            weight: 4
                        }
                    },
                    {
                        id: 'participant-files',
                        title: 'Upload and Download Participant Files',
                        href: 'https://files.countbubble.com/guides/Upload+and+Download+Participant+Files.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn how to upload and download files and documents for participants in your programs.',
                        emphasis: 'Includes link to a 4 minute video.',
                        landingTip: {
                            category: 'File Management',
                            icon: 'ri-upload-cloud-line',
                            title: 'Upload Participant Files',
                            description: 'Upload and download documents for participants in your programs.',
                            linkText: 'View Guide'
                        }
                    },
                    {
                        id: 'notes-feed',
                        title: 'Using the Notes Feed to Stay Organized',
                        href: 'https://files.countbubble.com/guides/Using+the+Notes+Feed+to+Stay+Organized.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn how the Notes Feed helps you add, edit, and manage the Notes you create anywhere in countbubble.',
                        emphasis: 'Includes link to a 6 minute video.',
                        landingTip: {
                            category: 'Organization',
                            icon: 'ri-sticky-note-line',
                            title: 'Use the Notes Feed',
                            description: 'Stay organized by adding, editing, and managing notes for your participants.',
                            linkText: 'View Guide'
                        }
                    },
                    {
                        id: 'plans',
                        title: 'Create, Edit, and Use Plans',
                        href: 'https://files.countbubble.com/guides/Using+Plans+in+countbubble.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn how to create Plans - aka case plans or coaching plans - add goals & tasks, edit plans, and use Plan reports.',
                        emphasis: 'Includes links to 3 videos.',
                        landingTip: {
                            category: 'Case Plans',
                            icon: 'ri-task-line',
                            title: 'Create & Use Plans',
                            description: 'Create case plans or coaching plans with goals and tasks for participants.',
                            linkText: 'View Guide'
                        }
                    },
                    {
                        id: 'delete-participant',
                        title: 'Delete a Participant',
                        href: 'https://files.countbubble.com/guides/Delete+a+Participant.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn how to delete a participant and how it impacts your data.',
                        emphasis: 'Includes link to a 2 minute video.'
                    },
                    {
                        id: 'delete-submitted-tracking-form',
                        title: 'Delete a Submitted Tracking Form',
                        href: 'https://files.countbubble.com/guides/Delete+a+Submitted+Tracking+Form.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn how to delete a submitted tracking form.',
                        emphasis: 'Includes link to a 3 minute video.'
                    },
                    {
                        id: 'public-intake-forms',
                        title: 'Create and Review Public Intake Forms',
                        href: 'https://files.countbubble.com/guides/Create+and+Review+Public+Intake+Forms.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn how to create a Public Intake (admins only) and review data submitted through them. (Paid Plans only)',
                        emphasis: 'Includes links to 2 videos.'
                    }
                ]
            }
        ]
    },
    {
        title: 'How To Guides for Admins Only',
        groups: [
            {
                title: 'Site Management',
                guides: [
                    {
                        id: 'create-organization',
                        title: 'Create an Organization',
                        href: 'https://files.countbubble.com/guides/Create+an+Organization.pdf',
                        linkSuffix: '(pdf)',
                        description: 'How to create your organization in countbubble.',
                        emphasis: 'Includes link to a 1 minute video.'
                    },
                    {
                        id: 'create-program',
                        title: 'Create a Program',
                        href: 'https://files.countbubble.com/guides/Create+a+Program.pdf',
                        linkSuffix: '(pdf)',
                        description: 'How to create programs for your organization.',
                        emphasis: 'Includes link to a 2 minute video.'
                    },
                    {
                        id: 'assign-form-program',
                        title: 'Assign a Form to a Program',
                        href: 'https://files.countbubble.com/guides/Assign+a+Form+to+a+Program.pdf',
                        linkSuffix: '(pdf)',
                        description: 'How and why you must assign forms to programs before you can use them for data entry.',
                        emphasis: 'Includes link to a 2 minute video.'
                    },
                    {
                        id: 'unassign-form-program',
                        title: 'Unassign a Form from a Program',
                        href: 'https://files.countbubble.com/guides/Unassign+a+Form+From+a+Program.pdf',
                        linkSuffix: '(pdf)',
                        description: 'How and why to enroll a participant in a program.',
                        emphasis: 'Includes link to a 2 minute video.'
                    },
                    {
                        id: 'create-user',
                        title: 'Create a User',
                        href: 'https://files.countbubble.com/guides/Create+a+User.pdf',
                        linkSuffix: '(pdf)',
                        description: 'How to create a new user in your organization.',
                        emphasis: 'Includes link to a 2 minute video.'
                    },
                    {
                        id: 'edit-user',
                        title: 'Edit an Existing User',
                        href: 'https://files.countbubble.com/guides/Edit+An+Existing+User.pdf',
                        linkSuffix: '(pdf)',
                        description: 'How to edit the details of users in your organization.',
                        emphasis: 'Includes link to a 2 minute video.'
                    },
                    {
                        id: 'delete-form-template',
                        title: 'Delete a Form Template',
                        href: 'https://files.countbubble.com/guides/Delete+a+Form+Template.pdf',
                        linkSuffix: '(pdf)',
                        description: 'How to delete a form template and how it impacts your data.',
                        emphasis: 'Includes link to a 3 minute video.'
                    },
                    {
                        id: 'delete-program',
                        title: 'Delete a Program',
                        href: 'https://files.countbubble.com/guides/Delete+a+Program.pdf',
                        linkSuffix: '(pdf)',
                        description: 'How to delete a program and how it impacts your data.',
                        emphasis: 'Includes link to a 3 minute video.'
                    },
                    {
                        id: 'export-data',
                        title: 'Export Data from countbubble',
                        href: 'https://files.countbubble.com/guides/Export+Data+from+countbubble.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn to export your data from countbubble.',
                        emphasis: 'Includes link to a 4.5 minute video.',
                        landingTip: {
                            category: 'Export Data',
                            icon: 'ri-file-list-3-line',
                            title: 'Export Your Data',
                            description: 'Learn to export your data from countbubble.',
                            linkText: 'View Guide'
                        }
                    },
                                        {
                        id: 'upgrade-paid-plan',
                        title: 'Upgrade from Free Trial to Paid Plan',
                        href: 'https://files.countbubble.com/guides/Upgrading+from+a+Free+Trial+to+a+Paid+Plan.pdf',
                        linkSuffix: '(pdf)',
                        description: 'How to upgrade from a free trial to a paid account. *Check our website for',
                        descriptionLink: {
                            href: 'https://www.countbubble.com/compare-plans',
                            text: 'current plan rates'
                        },
                        descriptionAfterLink: '.',
                        emphasis: 'Includes link to a 2 minute video.'
                    }
                ]
            },
            {
                title: 'Building and Managing Forms and Data',
                guides: [
                    {
                        id: 'create-outputs-outcomes-form',
                        title: 'Create an Outputs and Outcomes Form',
                        href: 'https://files.countbubble.com/guides/Create+an+Outputs+%26+Outcomes+Form.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Use the Form Builder to Create an Outputs & Outcomes form.',
                        emphasis: 'Includes link to a 6 minute video.'
                    },
                    {
                        id: 'form-builder-basics',
                        title: 'Form Builder Basics',
                        href: 'https://files.countbubble.com/guides/Form+Builder+Basics.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Detailed Explanation about the Form Builder with important tips about editing & updating forms.'
                    },
                    {
                        id: 'participant-profile-build',
                        title: 'The Participant Profile: Explanation and How to Build',
                        href: 'https://files.countbubble.com/guides/The+Participant+Profile+Explanation+and+How+To+Build.pdf',
                        linkSuffix: '(pdf)',
                        description: 'Learn the ins and outs of the participant profile.',
                        emphasis: 'Includes links to 3 videos.'
                    }
                ]
            }
        ]
    }
];

const landingGuideTips = helpGuideSections.flatMap(section =>
    section.groups.flatMap(group =>
        group.guides
            .filter(guide => guide.landingTip && guide.showOnLanding !== false && isGuideActive(guide))
            .map(guide => ({
                ...guide.landingTip!,
                linkUrl: guide.href,
                featuredOnLanding: guide.featuredOnLanding
            }))
    )
);

export const featuredLandingHelpGuideTips: LandingHelpGuideTip[] = landingGuideTips
    .filter(guide => guide.featuredOnLanding);

export const landingHelpGuideTips: LandingHelpGuideTip[] = landingGuideTips
    .filter(guide => !guide.featuredOnLanding);
